import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { reportClientError } from '../utils/clientLogger';
import { FloorPlanEditorHeader } from '../components/floorplan/FloorPlanEditorHeader';
import { FloorPlanEditorToolbar } from '../components/floorplan/FloorPlanEditorToolbar';
import { FloorPlanEditorCanvas } from '../components/floorplan/FloorPlanEditorCanvas';
import { FloorPlanSaveModal } from '../components/floorplan/FloorPlanSaveModal';
import { FloorPlanConsultaSuccessModal } from '../components/floorplan/FloorPlanConsultaSuccessModal';
import {
  ROOM_COLORS,
  ROOM_TYPES,
  FLOOR_PLAN_CANVAS_WIDTH,
  FLOOR_PLAN_CANVAS_HEIGHT,
  FLOOR_PLAN_GRID_SIZE,
  FLOOR_PLAN_SCALE,
  drawFloorPlanEditorCanvas,
} from '../utils/floorPlanEditorUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FloorPlanEditor = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [tool, setTool] = useState('wall'); // wall, room, select, delete
  const [walls, setWalls] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState(null);
  const [currentPoint, setCurrentPoint] = useState(null);
  const [selectedRoomType, setSelectedRoomType] = useState('living_room');
  const [roomPoints, setRoomPoints] = useState([]);
  const [showGrid, setShowGrid] = useState(true);
  const [saving, setSaving] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [savedProjectId, setSavedProjectId] = useState(null);
  const [consultaNumero, setConsultaNumero] = useState(null);
  const [requestingConsulta, setRequestingConsulta] = useState(false);
  const [showConsultaSuccess, setShowConsultaSuccess] = useState(false);

  const snapToGrid = (value) => Math.round(value / FLOOR_PLAN_GRID_SIZE) * FLOOR_PLAN_GRID_SIZE;

  const editorState = useMemo(
    () => ({
      showGrid,
      rooms,
      tool,
      roomPoints,
      currentPoint,
      walls,
      isDrawing,
      startPoint,
      selectedRoomType,
    }),
    [showGrid, rooms, tool, roomPoints, currentPoint, walls, isDrawing, startPoint, selectedRoomType]
  );

  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    drawFloorPlanEditorCanvas(canvas, editorState);
  }, [editorState]);

  useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);

  const getMousePos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: snapToGrid((e.clientX - rect.left) * scaleX),
      y: snapToGrid((e.clientY - rect.top) * scaleY)
    };
  };

  const handleMouseDown = (e) => {
    const pos = getMousePos(e);

    if (tool === 'wall') {
      setIsDrawing(true);
      setStartPoint(pos);
      setCurrentPoint(pos);
    } else if (tool === 'room') {
      // Check if clicking near first point to close the room
      if (roomPoints.length >= 3) {
        const firstPoint = roomPoints[0];
        const dist = Math.sqrt(
          Math.pow(pos.x - firstPoint.x, 2) + Math.pow(pos.y - firstPoint.y, 2)
        );
        if (dist < 20) {
          // Close the room
          const roomLabel = ROOM_TYPES.find(r => r.value === selectedRoomType)?.label || 'Cômodo';
          const newRoom = {
            id: crypto.randomUUID(),
            name: roomLabel,
            room_type: selectedRoomType,
            points: [...roomPoints],
            color: ROOM_COLORS[selectedRoomType]
          };
          setRooms([...rooms, newRoom]);
          setRoomPoints([]);
          return;
        }
      }
      setRoomPoints([...roomPoints, pos]);
    } else if (tool === 'delete') {
      // Delete wall or room near click
      const newWalls = walls.filter(wall => {
        const midX = (wall.start_x + wall.end_x) / 2;
        const midY = (wall.start_y + wall.end_y) / 2;
        const dist = Math.sqrt(Math.pow(pos.x - midX, 2) + Math.pow(pos.y - midY, 2));
        return dist > 30;
      });
      setWalls(newWalls);

      const newRooms = rooms.filter(room => {
        const centerX = room.points.reduce((sum, p) => sum + p.x, 0) / room.points.length;
        const centerY = room.points.reduce((sum, p) => sum + p.y, 0) / room.points.length;
        const dist = Math.sqrt(Math.pow(pos.x - centerX, 2) + Math.pow(pos.y - centerY, 2));
        return dist > 30;
      });
      setRooms(newRooms);
    }
  };

  const handleMouseMove = (e) => {
    const pos = getMousePos(e);
    setCurrentPoint(pos);
  };

  const handleMouseUp = (e) => {
    if (tool === 'wall' && isDrawing && startPoint) {
      const pos = getMousePos(e);
      if (Math.abs(pos.x - startPoint.x) > 10 || Math.abs(pos.y - startPoint.y) > 10) {
        const newWall = {
          id: crypto.randomUUID(),
          start_x: startPoint.x,
          start_y: startPoint.y,
          end_x: pos.x,
          end_y: pos.y,
          thickness: 10
        };
        setWalls([...walls, newWall]);
      }
    }
    setIsDrawing(false);
    setStartPoint(null);
  };

  const clearCanvas = () => {
    setWalls([]);
    setRooms([]);
    setRoomPoints([]);
  };

  const saveProject = async () => {
    if (!projectName || !userEmail) {
      alert('Por favor, preencha nome do projeto e email');
      return;
    }

    setSaving(true);
    try {
      const floorPlan = {
        walls,
        rooms,
        width: FLOOR_PLAN_CANVAS_WIDTH,
        height: FLOOR_PLAN_CANVAS_HEIGHT,
        scale: FLOOR_PLAN_SCALE
      };

      const response = await axios.post(`${API}/projects`, {
        name: projectName,
        user_email: userEmail,
        user_name: userName,
        description: '',
        floor_plan: floorPlan
      });

      setSavedProjectId(response.data.id);
      setShowSaveModal(false);
      alert('Projeto salvo com sucesso!');
    } catch (error) {
      reportClientError('Error saving project:', error);
      alert('Erro ao salvar projeto');
    } finally {
      setSaving(false);
    }
  };

  const requestConsulta = async () => {
    if (!savedProjectId) {
      setShowSaveModal(true);
      return;
    }

    setRequestingConsulta(true);
    try {
      const response = await axios.post(`${API}/consulta-reforma?project_id=${savedProjectId}`);
      setConsultaNumero(response.data.numero_consulta);
      setShowConsultaSuccess(true);
    } catch (error) {
      reportClientError('Error requesting consulta:', error);
      alert('Erro ao solicitar consulta. Tente novamente.');
    } finally {
      setRequestingConsulta(false);
    }
  };

  const view3D = () => {
    if (savedProjectId) {
      navigate(`/render-3d/${savedProjectId}`);
    } else {
      setShowSaveModal(true);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100" data-testid="floorplan-editor-page">
      <FloorPlanEditorHeader
        onBack={() => navigate('/')}
        onClear={clearCanvas}
        onOpenSave={() => setShowSaveModal(true)}
        onView3D={view3D}
        onRequestConsulta={requestConsulta}
        requestingConsulta={requestingConsulta}
      />

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex gap-6" data-testid="floorplan-editor-layout">
          <FloorPlanEditorToolbar
            tool={tool}
            setTool={setTool}
            roomPoints={roomPoints}
            setRoomPoints={setRoomPoints}
            showGrid={showGrid}
            setShowGrid={setShowGrid}
            selectedRoomType={selectedRoomType}
            setSelectedRoomType={setSelectedRoomType}
            walls={walls}
            rooms={rooms}
            roomTypes={ROOM_TYPES}
            roomColors={ROOM_COLORS}
          />

          <FloorPlanEditorCanvas
            canvasRef={canvasRef}
            width={FLOOR_PLAN_CANVAS_WIDTH}
            height={FLOOR_PLAN_CANVAS_HEIGHT}
            scale={FLOOR_PLAN_SCALE}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={() => setIsDrawing(false)}
          />
        </div>
      </div>

      <FloorPlanSaveModal
        isOpen={showSaveModal}
        projectName={projectName}
        setProjectName={setProjectName}
        userEmail={userEmail}
        setUserEmail={setUserEmail}
        userName={userName}
        setUserName={setUserName}
        onClose={() => setShowSaveModal(false)}
        onSave={saveProject}
        saving={saving}
      />

      <FloorPlanConsultaSuccessModal
        isOpen={showConsultaSuccess}
        consultaNumero={consultaNumero}
        onClose={() => setShowConsultaSuccess(false)}
        onBackToSite={() => navigate('/')}
      />
    </div>
  );
};

export default FloorPlanEditor;
